import DashboardLayout from "@/components/DashboardLayout";
import { PermissionGuard } from "@/components/PermissionGuard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { trpc } from "@/lib/trpc";
import { useContextoDaTela } from "@/features/assistente/contexto-de-tela";
import { useLocation, useParams } from "wouter";
import { ScoreCard } from "@/components/ScoreBadge";
import {
  ArrowLeft, Phone, Mail, Building2, MapPin, Tag, MessageSquare,
  CheckCircle, XCircle, Clock, Send, User, Calendar, Edit2,
  Landmark, Briefcase, ChevronRight, Users, LayoutGrid, Eye, Target,
  Activity, CalendarClock, Megaphone,
} from "lucide-react";
import ContactTimeline from "@/components/ContactTimeline";
import MeetingsCard from "@/components/Agenda/MeetingsCard";
import { Link } from "wouter";
import { useState } from "react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const BRAZIL_STATES = [
  "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS",
  "MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"
];

const ORGAN_TYPES = [
  "Prefeitura", "Câmara Municipal", "Autarquia", "Governo Estadual",
  "Assembleia Legislativa", "Tribunal", "Fundação Pública", "Outro"
];

type ContactType = "public_admin" | "empresa" | "cliente_final";

const CONTACT_TYPE_LABELS: Record<string, string> = {
  public_admin: "Administração Pública",
  empresa: "Empresa",
  cliente_final: "Cliente Final",
};

const CONTACT_TYPE_ICONS: Record<string, any> = {
  public_admin: Landmark,
  empresa: Briefcase,
  cliente_final: User,
};

function MessageStatusIcon({ status }: { status: string }) {
  const map: Record<string, { icon: any; color: string; label: string }> = {
    sent: { icon: Send, color: "text-blue-500", label: "Enviado" },
    delivered: { icon: CheckCircle, color: "text-green-500", label: "Entregue" },
    read: { icon: CheckCircle, color: "text-primary", label: "Lido" },
    failed: { icon: XCircle, color: "text-destructive", label: "Falhou" },
    pending: { icon: Clock, color: "text-muted-foreground", label: "Pendente" },
  };
  const cfg = map[status] ?? map.pending;
  return <cfg.icon className={`h-3.5 w-3.5 ${cfg.color}`} title={cfg.label} />;
}

// ─── Breadcrumb hierárquico ───────────────────────────────────────────────────

function HierarchyBreadcrumb({ contact }: { contact: any }) {
  if (contact.contactType !== "public_admin") return null;
  type Part = { label: string; href?: string };
  const parts: Part[] = [
    contact.state ? { label: contact.state } : null,
    contact.city ? { label: contact.city } : null,
    contact.companyName
      ? {
          label: contact.companyName,
          // Vai para a página da organização no CRM
          href: `/crm/organizations/${encodeURIComponent(contact.companyName)}`,
        }
      : null,
    contact.sector ? { label: contact.sector } : null,
    contact.name ? { label: contact.name } : null,
  ].filter((x): x is Part => x !== null);

  if (parts.length < 2) return null;

  return (
    <div className="flex items-center gap-1 overflow-x-auto text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2 whitespace-nowrap">
      <Landmark className="h-3 w-3 shrink-0" />
      {parts.map((part, i) => (
        <span key={i} className="flex items-center gap-1 shrink-0">
          {i > 0 && <ChevronRight className="h-3 w-3" />}
          {part.href ? (
            <a
              href={part.href}
              className={`hover:text-primary hover:underline ${
                i === parts.length - 1 ? "font-medium text-foreground" : ""
              } max-w-[180px] sm:max-w-none truncate inline-block`}
            >
              {part.label}
            </a>
          ) : (
            <span
              className={`${
                i === parts.length - 1 ? "font-medium text-foreground" : ""
              } max-w-[180px] sm:max-w-none truncate`}
            >
              {part.label}
            </span>
          )}
        </span>
      ))}
    </div>
  );
}

export default function ContactDetail() {
  const params = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const contactId = Number(params.id);
  const [showEdit, setShowEdit] = useState(false);
  const utils = trpc.useUtils();

  const { data: contact, isLoading } = trpc.contacts.get.useQuery({ id: contactId }, { enabled: !!contactId });
  const { data: contactTags } = trpc.contacts.getTags.useQuery({ contactId }, { enabled: !!contactId });

  // A ficha aberta: é o que faz "adicione a tag X neste contato" funcionar
  // sem a pessoa dizer o id.
  useContextoDaTela(
    contact
      ? {
          titulo: `Contato: ${(contact as any).name ?? contactId}`,
          dados: {
            contactId,
            nome: (contact as any).name,
            empresa: (contact as any).companyName ?? null,
            cidade: (contact as any).city ?? null,
            uf: (contact as any).state ?? null,
            tipo: (contact as any).contactType ?? null,
            tags: (contactTags ?? []).map((t: any) => t.name ?? t.tag?.name).filter(Boolean),
          },
        }
      : null,
  );

  const { data: allTags } = trpc.tags.list.useQuery();
  const { data: messages } = trpc.contacts.getMessages.useQuery({ contactId }, { enabled: !!contactId });
  const { data: leadScore } = trpc.leadScoring.getScore.useQuery({ contactId }, { enabled: !!contactId });
  const { data: fieldDefs = [] } = trpc.customFields.listDefs.useQuery();
  const { data: fieldValues = [] } = trpc.customFields.getContactValues.useQuery({ contactId }, { enabled: !!contactId });
  const setFieldValueMut = trpc.customFields.setContactValue.useMutation({
    onSuccess: () => utils.customFields.getContactValues.invalidate({ contactId }),
  });
  const [editingField, setEditingField] = useState<number | null>(null);
  const [fieldInput, setFieldInput] = useState("");
  const { data: emailHistory } = trpc.sendgrid.history.byContact.useQuery(
    { contactId: String(contactId) },
    { enabled: !!contactId }
  );

  const [editForm, setEditForm] = useState<any>(null);

  const updateMut = trpc.contacts.update.useMutation({
    onSuccess: () => {
      utils.contacts.get.invalidate({ id: contactId });
      toast.success("Contato atualizado!");
      setShowEdit(false);
    },
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const addTagMut = trpc.contacts.addTag.useMutation({
    onSuccess: () => utils.contacts.getTags.invalidate({ contactId }),
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const removeTagMut = trpc.contacts.removeTag.useMutation({
    onSuccess: () => utils.contacts.getTags.invalidate({ contactId }),
    onError: (e) => toast.error(toUserFriendlyError(e)),
  });

  const openEdit = () => {
    if (!contact) return;
    setEditForm({
      name: contact.name,
      phoneNumber: contact.phoneNumber ?? "",
      email: contact.email ?? "",
      contactType: (contact as any).contactType ?? "public_admin",
      companyName: contact.companyName ?? "",
      companyType: contact.companyType ?? "",
      sector: (contact as any).sector ?? "",
      jobTitle: (contact as any).jobTitle ?? "",
      city: contact.city ?? "",
      state: contact.state ?? "none",
      notes: contact.notes ?? "",
    });
    setShowEdit(true);
  };

  const handleSaveEdit = () => {
    // Contato importado de lista institucional pode não ter telefone. Exigir
    // telefone aqui deixaria esse contato impossível de editar — nem o nome.
    if (!editForm?.name) { toast.error("Nome é obrigatório"); return; }
    if (!editForm?.phoneNumber && !editForm?.email) { toast.error("Informe telefone ou e-mail"); return; }
    updateMut.mutate({
      id: contactId,
      name: editForm.name,
      phoneNumber: editForm.phoneNumber,
      email: editForm.email || undefined,
      contactType: editForm.contactType || undefined,
      companyName: editForm.companyName || undefined,
      companyType: editForm.companyType || undefined,
      sector: editForm.sector || undefined,
      jobTitle: editForm.jobTitle || undefined,
      city: editForm.city || undefined,
      state: editForm.state === "none" ? undefined : editForm.state,
      notes: editForm.notes || undefined,
    });
  };

  const tagIds = new Set(contactTags?.map((t: any) => t.id) ?? []);
  const toggleTag = (tagId: number) => {
    if (tagIds.has(tagId)) removeTagMut.mutate({ contactId, tagId });
    else addTagMut.mutate({ contactId, tagId });
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="space-y-4 max-w-4xl">
          <div className="h-8 w-48 bg-muted animate-pulse rounded" />
          <div className="h-40 bg-muted animate-pulse rounded-xl" />
          <div className="h-60 bg-muted animate-pulse rounded-xl" />
        </div>
      </DashboardLayout>
    );
  }

  if (!contact) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <User className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground font-medium">Contato não encontrado</p>
          <Button size="sm" className="mt-4" onClick={() => navigate("/contacts")}>
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Voltar para Contatos
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const contactType = (contact as any).contactType as string ?? "public_admin";
  const sector = (contact as any).sector as string | null;
  const jobTitle = (contact as any).jobTitle as string | null;
  const TypeIcon = CONTACT_TYPE_ICONS[contactType] ?? User;
  const isPublicAdmin = contactType === "public_admin";

  return (
    <PermissionGuard permission="contacts:view">
    <DashboardLayout>
      <div className="space-y-5 max-w-4xl">
        {/* Header */}
        <div className="flex items-start gap-2 sm:gap-3 flex-wrap">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => navigate("/contacts")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1 min-w-0 order-1 w-full sm:w-auto">
            <div className="flex items-center gap-2 mb-0.5">
              <TypeIcon className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="text-xs text-muted-foreground truncate">
                {CONTACT_TYPE_LABELS[contactType] ?? contactType}
              </span>
            </div>
            <h1 className="text-lg sm:text-xl font-bold truncate">{contact.name}</h1>
            {contact.phoneNumber && (
              <p className="text-xs sm:text-sm text-muted-foreground font-mono break-all">
                {contact.phoneNumber}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-auto sm:ml-0">
            {contact.optedOut ? (
              <Badge variant="destructive" className="text-xs">Opt-out</Badge>
            ) : (
              <Badge className="text-xs bg-green-100 text-green-700 border-green-200">
                Ativo
              </Badge>
            )}
            <Button size="sm" variant="outline" onClick={openEdit}>
              <Edit2 className="h-3.5 w-3.5 sm:mr-1.5" />
              <span className="hidden sm:inline">Editar</span>
            </Button>
          </div>
        </div>

        {/* Hierarquia breadcrumb (somente Adm. Pública) */}
        <HierarchyBreadcrumb contact={contact} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-5">
          {/* Informações do Contato / E-mails */}
          <div className="lg:col-span-2 min-w-0">
            <Tabs defaultValue="timeline">
              <div className="overflow-x-auto -mx-1 px-1 mb-4">
                <TabsList className="w-max">
                  <TabsTrigger value="timeline" className="flex items-center gap-1.5">
                    <Activity className="h-3.5 w-3.5" /> Timeline
                  </TabsTrigger>
                  <TabsTrigger value="whatsapp" className="flex items-center gap-1.5">
                    <MessageSquare className="h-3.5 w-3.5" /> WhatsApp
                  </TabsTrigger>
                  <TabsTrigger value="emails" className="flex items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5" /> E-mails
                  </TabsTrigger>
                  <TabsTrigger value="deals" className="flex items-center gap-1.5">
                    <Target className="h-3.5 w-3.5" /> Oportunidades
                  </TabsTrigger>
                  <TabsTrigger value="reunioes" className="flex items-center gap-1.5">
                    <CalendarClock className="h-3.5 w-3.5" /> Reuniões
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="timeline" className="mt-0">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Activity className="h-4 w-4 text-primary" /> Histórico completo
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ContactTimeline contactId={contact.id} />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="whatsapp" className="space-y-4 mt-0">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <TypeIcon className="h-4 w-4 text-primary" />
                  {isPublicAdmin ? "Dados da Administração Pública" : "Informações"}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Telefone */}
                  <div className="flex items-start gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Telefone / WhatsApp</p>
                      <p className={contact.phoneNumber ? "text-sm font-medium font-mono" : "text-sm italic text-muted-foreground"}>
                        {contact.phoneNumber || "não informado"}
                      </p>
                    </div>
                  </div>

                  {/* E-mail */}
                  {contact.email && (
                    <div className="flex items-start gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">E-mail</p>
                        <p className="text-sm font-medium truncate">{contact.email}</p>
                      </div>
                    </div>
                  )}

                  {/* Órgão / Empresa */}
                  {contact.companyName && (
                    <div className="flex items-start gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">
                          {isPublicAdmin ? "Órgão" : "Empresa"}
                        </p>
                        <p className="text-sm font-medium">{contact.companyName}</p>
                        {contact.companyType && (
                          <Badge variant="secondary" className="text-xs mt-0.5">{contact.companyType}</Badge>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Setor (Adm. Pública) */}
                  {sector && (
                    <div className="flex items-start gap-2">
                      <LayoutGrid className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">Setor / Secretaria</p>
                        <p className="text-sm font-medium">{sector}</p>
                      </div>
                    </div>
                  )}

                  {/* Cargo — da pessoa, existe nos três tipos de contato */}
                  {jobTitle && (
                    <div className="flex items-start gap-2">
                      <Briefcase className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">Cargo</p>
                        <p className="text-sm font-medium">{jobTitle}</p>
                      </div>
                    </div>
                  )}

                  {/* Localização */}
                  {(contact.city || contact.state) && (
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">Localização</p>
                        <p className="text-sm font-medium">
                          {[contact.city, contact.state].filter(Boolean).join(" — ")}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Data de cadastro */}
                  <div className="flex items-start gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div>
                      <p className="text-xs text-muted-foreground">Cadastrado em</p>
                      <p className="text-sm font-medium">
                        {new Date(contact.createdAt).toLocaleDateString("pt-BR")}
                      </p>
                    </div>
                  </div>

                  {/* Origem: anúncio pelo qual o contato chegou (primeiro toque) */}
                  {contact.firstAdId && (
                    <div className="flex items-start gap-2">
                      <Megaphone className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs text-muted-foreground">Origem</p>
                        <p className="text-sm font-medium">
                          Anúncio Meta
                          {(() => {
                            const r = (contact.firstAdReferral ?? {}) as { headline?: string | null; adName?: string | null; campaignName?: string | null; sourceUrl?: string | null };
                            const nome = r.headline ?? r.adName;
                            return nome ? ` · ${nome}` : ` · ${contact.firstAdId}`;
                          })()}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {(contact.firstAdReferral as { campaignName?: string | null } | null)?.campaignName ?? ""}
                          {contact.firstAdReferralAt ? ` ${new Date(contact.firstAdReferralAt).toLocaleDateString("pt-BR")}` : ""}
                          {(contact.firstAdReferral as { sourceUrl?: string | null } | null)?.sourceUrl && (
                            <>
                              {" · "}
                              <a href={(contact.firstAdReferral as { sourceUrl?: string }).sourceUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">ver anúncio</a>
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Última interação */}
                  {contact.lastInteractionAt && (
                    <div className="flex items-start gap-2">
                      <MessageSquare className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">Última interação</p>
                        <p className="text-sm font-medium">
                          {new Date(contact.lastInteractionAt).toLocaleDateString("pt-BR")}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Hierarquia visual (Adm. Pública) */}
                {isPublicAdmin && (contact.state || contact.city || contact.companyName || sector) && (
                  <div className="pt-3 border-t">
                    <p className="text-xs text-muted-foreground mb-2 font-medium">Hierarquia</p>
                    <div className="space-y-1">
                      {[
                        { label: "Estado", value: contact.state, indent: 0 },
                        { label: "Cidade", value: contact.city, indent: 1 },
                        { label: contact.companyType || "Órgão", value: contact.companyName, indent: 2 },
                        { label: "Setor", value: sector, indent: 3 },
                        { label: "Contato", value: contact.name, indent: 4, highlight: true },
                      ].filter(item => item.value).map((item, i) => (
                        <div key={i} className="flex items-center gap-1.5" style={{ paddingLeft: item.indent * 16 }}>
                          <ChevronRight className="h-3 w-3 text-muted-foreground/50 shrink-0" />
                          <span className="text-xs text-muted-foreground">{item.label}:</span>
                          <span className={`text-xs ${item.highlight ? "font-semibold text-primary" : "font-medium"}`}>{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Observações */}
                {contact.notes && (
                  <div className="pt-3 border-t">
                    <p className="text-xs text-muted-foreground mb-1">Observações</p>
                    <p className="text-sm text-muted-foreground">{contact.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Histórico de Mensagens */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <MessageSquare className="h-4 w-4 text-primary" /> Histórico de Mensagens
                  <Badge variant="secondary" className="ml-auto text-xs">{(messages as any[])?.length ?? 0}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!(messages as any[])?.length ? (
                  <div className="text-center py-8">
                    <MessageSquare className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                    <p className="text-sm text-muted-foreground">Nenhuma mensagem enviada ainda</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      As mensagens enviadas via campanhas aparecerão aqui
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                    {(messages as any[]).map((msg: any) => (
                      <div key={msg.id} className="flex items-start gap-3 p-2.5 rounded-lg bg-muted/40 hover:bg-muted/60 transition-colors">
                        <div className="shrink-0 mt-0.5">
                          <MessageStatusIcon status={msg.status} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-xs font-medium truncate">{msg.templateName ?? "Mensagem"}</p>
                            <span className="text-xs text-muted-foreground shrink-0">
                              {new Date(msg.sentAt ?? msg.createdAt).toLocaleDateString("pt-BR")}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 truncate">{msg.campaignName ?? "Disparo direto"}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
              </TabsContent>

              <TabsContent value="emails" className="mt-0">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Mail className="h-4 w-4 text-primary" /> Histórico de E-mails
                      <Badge variant="secondary" className="ml-auto text-xs">{(emailHistory as any[])?.length ?? 0}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {!(emailHistory as any[])?.length ? (
                      <div className="text-center py-8">
                        <Mail className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                        <p className="text-sm text-muted-foreground">Nenhum e-mail enviado ainda</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Os e-mails enviados via campanhas aparecerão aqui
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                        {(emailHistory as any[]).map((email: any) => {
                          const statusMap: Record<string, { icon: any; color: string; label: string; badgeClass: string }> = {
                            sent:      { icon: Mail,         color: "text-blue-500",        label: "Enviado",   badgeClass: "bg-blue-100 text-blue-700" },
                            pending:   { icon: Clock,        color: "text-muted-foreground", label: "Pendente",  badgeClass: "bg-muted text-muted-foreground" },
                            delivered: { icon: CheckCircle,  color: "text-green-500",        label: "Entregue",  badgeClass: "bg-green-100 text-green-700" },
                            opened:    { icon: Eye,          color: "text-primary",          label: "Aberto",    badgeClass: "bg-primary/10 text-primary" },
                            clicked:   { icon: CheckCircle,  color: "text-primary",          label: "Clicado",   badgeClass: "bg-primary/10 text-primary" },
                            bounced:   { icon: XCircle,      color: "text-destructive",      label: "Bounce",    badgeClass: "bg-red-100 text-red-700" },
                            failed:    { icon: XCircle,      color: "text-destructive",      label: "Falhou",    badgeClass: "bg-red-100 text-red-700" },
                          };
                          const cfg = statusMap[email.status] ?? statusMap.pending;
                          const StatusIcon = cfg.icon;
                          return (
                            <div key={email.id} className="flex items-start gap-3 p-2.5 rounded-lg bg-muted/40 hover:bg-muted/60 transition-colors">
                              <div className="shrink-0 mt-0.5">
                                <StatusIcon className={`h-3.5 w-3.5 ${cfg.color}`} title={cfg.label} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                  <p className="text-xs font-medium truncate">{email.subject ?? "(sem assunto)"}</p>
                                  <span className="text-xs text-muted-foreground shrink-0">
                                    {new Date(email.sentAt ?? email.createdAt).toLocaleDateString("pt-BR")}
                                  </span>
                                </div>
                                <div className="mt-0.5">
                                  <Badge variant="secondary" className={`text-xs ${cfg.badgeClass}`}>{cfg.label}</Badge>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="deals" className="mt-0">
                <ContactDealsPanel contactId={contact.id} />
              </TabsContent>

              <TabsContent value="reunioes" className="mt-0">
                <MeetingsCard contactId={contact.id} contactName={contact.name} />
              </TabsContent>
            </Tabs>
          </div>

          {/* Coluna lateral */}
          <div className="space-y-4">
            {/* Lead Score */}
            {leadScore && (
              <ScoreCard
                score={leadScore.totalScore}
                contactName={contact.name}
                lastActivity={leadScore.lastActivityAt}
                breakdown={leadScore.scoreBreakdown}
              />
            )}

            {/* Tags */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Tag className="h-4 w-4 text-primary" /> Tags
                  {contactTags && contactTags.length > 0 && (
                    <Badge variant="secondary" className="ml-auto text-xs">{contactTags.length}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Tags aplicadas */}
                {contactTags && contactTags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {contactTags.map((tag: any) => (
                      <Badge
                        key={tag.id}
                        className="text-xs px-2 py-0.5"
                        style={{ backgroundColor: tag.color, color: "white" }}
                      >
                        {tag.name}
                      </Badge>
                    ))}
                  </div>
                )}
                {(allTags?.length ?? 0) === 0 ? (
                  <div className="text-center py-6">
                    <Tag className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                    <p className="text-xs text-muted-foreground">Nenhuma tag criada.</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Crie tags na página de <strong>Tags</strong> primeiro.</p>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <p className="text-xs text-muted-foreground mb-2">Clique para adicionar ou remover:</p>
                    {allTags?.map((tag: any) => {
                      const active = tagIds.has(tag.id);
                      const isTagLoading = (addTagMut.isPending || removeTagMut.isPending);
                      return (
                        <button
                          key={tag.id}
                          onClick={() => !isTagLoading && toggleTag(tag.id)}
                          disabled={isTagLoading}
                          className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-all border cursor-pointer ${
                            active
                              ? "font-semibold shadow-sm"
                              : "hover:bg-muted/60 border-dashed border-muted-foreground/30 bg-muted/20 text-muted-foreground hover:text-foreground"
                          }`}
                          style={active ? {
                            backgroundColor: (tag.color ?? "#25D366") + "20",
                            borderColor: (tag.color ?? "#25D366") + "60",
                            color: tag.color ?? "#25D366",
                          } : {}}
                        >
                          <div className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: tag.color ?? "#25D366" }} />
                          <span className="flex-1 text-left truncate">{tag.name}</span>
                          {active ? <CheckCircle className="h-4 w-4 shrink-0" /> : <span className="text-xs opacity-60">+ adicionar</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Campos Personalizados */}
            {(fieldDefs as any[]).filter((f: any) => (f.scope ?? "contact") === "contact").length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <LayoutGrid className="h-4 w-4 text-primary" /> Campos Personalizados
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(fieldDefs as any[]).filter((f: any) => (f.scope ?? "contact") === "contact").map((def: any) => {
                    const val = (fieldValues as any[]).find((v: any) => v.fieldId === def.id);
                    const currentValue = val?.value ?? def.defaultValue ?? "";
                    const isEditing = editingField === def.id;

                    // Boolean: toggle inline sem modo edição
                    if (def.fieldType === "boolean") {
                      return (
                        <div key={def.id} className="flex items-center justify-between">
                          <p className="text-xs text-muted-foreground">{def.name}</p>
                          <Switch
                            checked={currentValue === "true"}
                            onCheckedChange={(checked) => {
                              setFieldValueMut.mutate({ contactId, fieldId: def.id, value: String(checked) });
                            }}
                          />
                        </div>
                      );
                    }

                    // Select: dropdown inline
                    if (def.fieldType === "select" && def.options) {
                      return (
                        <div key={def.id} className="space-y-1">
                          <p className="text-xs text-muted-foreground">{def.name}</p>
                          <Select
                            value={currentValue}
                            onValueChange={(v) => setFieldValueMut.mutate({ contactId, fieldId: def.id, value: v })}
                          >
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue placeholder="Selecione..." />
                            </SelectTrigger>
                            <SelectContent>
                              {(def.options as string[]).map((opt: string) => (
                                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    }

                    // Text, Number, Date: edição inline com input tipado
                    return (
                      <div key={def.id} className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-muted-foreground">{def.name}</p>
                          {isEditing ? (
                            <div className="flex gap-1 mt-1">
                              <Input
                                className="h-7 text-xs"
                                type={def.fieldType === "number" ? "number" : def.fieldType === "date" ? "date" : "text"}
                                value={fieldInput}
                                onChange={e => setFieldInput(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === "Enter") {
                                    setFieldValueMut.mutate({ contactId, fieldId: def.id, value: fieldInput });
                                    setEditingField(null);
                                  }
                                  if (e.key === "Escape") setEditingField(null);
                                }}
                                autoFocus
                              />
                              <Button size="sm" className="h-7 px-2 text-xs" onClick={() => { setFieldValueMut.mutate({ contactId, fieldId: def.id, value: fieldInput }); setEditingField(null); }}>OK</Button>
                            </div>
                          ) : (
                            <p
                              className="text-sm font-medium cursor-pointer hover:text-primary"
                              onClick={() => { setEditingField(def.id); setFieldInput(currentValue); }}
                            >
                              {currentValue || <span className="text-muted-foreground italic text-xs">Clique para editar</span>}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* Estatísticas */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Estatísticas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {[
                  { label: "Mensagens enviadas", value: (messages as any[])?.length ?? 0, color: "text-blue-600" },
                  { label: "Entregues", value: (messages as any[])?.filter((m: any) => m.status === "delivered" || m.status === "read").length ?? 0, color: "text-green-600" },
                  { label: "Lidas", value: (messages as any[])?.filter((m: any) => m.status === "read").length ?? 0, color: "text-primary" },
                ].map(stat => (
                  <div key={stat.label} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{stat.label}</span>
                    <span className={`font-semibold ${stat.color}`}>{stat.value}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={(o) => { if (!o) setShowEdit(false); }}>
        <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto mx-2 sm:mx-0">
          <DialogHeader>
            <DialogTitle>Editar Contato</DialogTitle>
          </DialogHeader>
          {editForm && (
            <div className="space-y-3 py-2">
              {/* Tipo de contato */}
              <div>
                <Label className="mb-2 block">Tipo de Contato</Label>
                <div className="grid grid-cols-3 gap-2">
                  {(["public_admin", "empresa", "cliente_final"] as const).map((type) => {
                    const Icon = CONTACT_TYPE_ICONS[type];
                    return (
                      <button
                        key={type}
                        onClick={() => setEditForm((f: any) => ({ ...f, contactType: type }))}
                        className={`flex flex-col items-center gap-1 p-2.5 rounded-lg border-2 text-xs font-medium transition-colors ${editForm.contactType === type ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/40"}`}
                      >
                        <Icon className="w-4 h-4" />
                        {CONTACT_TYPE_LABELS[type]}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Nome *</Label>
                  <Input value={editForm.name} onChange={(e) => setEditForm((f: any) => ({ ...f, name: e.target.value }))} className="mt-1" />
                </div>
                <div className="col-span-2">
                  <Label>Telefone *</Label>
                  <Input value={editForm.phoneNumber} onChange={(e) => setEditForm((f: any) => ({ ...f, phoneNumber: e.target.value }))} className="mt-1" />
                </div>
                <div className="col-span-2">
                  <Label>E-mail</Label>
                  <Input type="email" value={editForm.email} onChange={(e) => setEditForm((f: any) => ({ ...f, email: e.target.value }))} className="mt-1" />
                </div>

                {editForm.contactType === "public_admin" ? (
                  <>
                    <div className="col-span-2">
                      <Label>Órgão (Prefeitura, Câmara, Autarquia...)</Label>
                      <Input
                        placeholder="Ex: Prefeitura Municipal de Campinas"
                        value={editForm.companyName}
                        onChange={(e) => setEditForm((f: any) => ({ ...f, companyName: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Tipo de Órgão</Label>
                      <select
                        className="w-full mt-1 px-3 py-2 border border-border rounded-md text-sm bg-background"
                        value={editForm.companyType}
                        onChange={(e) => setEditForm((f: any) => ({ ...f, companyType: e.target.value }))}
                      >
                        <option value="">Selecione...</option>
                        {ORGAN_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label>Setor / Secretaria</Label>
                      <Input
                        placeholder="Ex: Sec. de Administração"
                        value={editForm.sector}
                        onChange={(e) => setEditForm((f: any) => ({ ...f, sector: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-span-2">
                      <Label>{editForm.contactType === "empresa" ? "Nome da Empresa" : "Empresa / Organização"}</Label>
                      <Input
                        value={editForm.companyName}
                        onChange={(e) => setEditForm((f: any) => ({ ...f, companyName: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    {editForm.contactType === "empresa" && (
                      <div className="col-span-2">
                        <Label>Segmento / Ramo</Label>
                        <Input
                          placeholder="Ex: Tecnologia, Saúde..."
                          value={editForm.companyType}
                          onChange={(e) => setEditForm((f: any) => ({ ...f, companyType: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                    )}
                  </>
                )}

                {/* Cargo — fora do ramo por tipo: é da pessoa, existe nos três */}
                <div className="col-span-2">
                  <Label>Cargo</Label>
                  <Input
                    placeholder="Ex: Pregoeiro, Secretário, Diretor de Compras"
                    value={editForm.jobTitle}
                    onChange={(e) => setEditForm((f: any) => ({ ...f, jobTitle: e.target.value }))}
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Cidade</Label>
                  <Input value={editForm.city} onChange={(e) => setEditForm((f: any) => ({ ...f, city: e.target.value }))} className="mt-1" />
                </div>
                <div>
                  <Label>Estado</Label>
                  <Select value={editForm.state} onValueChange={(v) => setEditForm((f: any) => ({ ...f, state: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="UF" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {BRAZIL_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Observações</Label>
                  <Textarea value={editForm.notes} onChange={(e) => setEditForm((f: any) => ({ ...f, notes: e.target.value }))} className="mt-1 min-h-[70px]" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)}>Cancelar</Button>
            <Button onClick={handleSaveEdit} disabled={updateMut.isPending}>
              {updateMut.isPending ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
    </PermissionGuard>
  );
}

// ─── Oportunidades do contato (CRM) ──────────────────────────────────────────
function ContactDealsPanel({ contactId }: { contactId: number }) {
  const dealsQuery = trpc.crm.deals.listByContact.useQuery({ contactId });
  const pipelinesQuery = trpc.crm.pipelines.list.useQuery({});
  const pipelines = pipelinesQuery.data ?? [];

  function formatBRL(raw: string | number | null | undefined) {
    const n = typeof raw === "string" ? Number(raw) : raw ?? 0;
    if (Number.isNaN(n)) return "R$ 0,00";
    return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  }

  const rows = dealsQuery.data ?? [];
  const openDeals = rows.filter((d: any) => d.status === "open");
  const wonDeals = rows.filter((d: any) => d.status === "won");
  const lostDeals = rows.filter((d: any) => d.status === "lost");
  const totalOpen = openDeals.reduce(
    (acc: number, d: any) => acc + Number(d.value ?? 0),
    0,
  );
  const totalWon = wonDeals.reduce(
    (acc: number, d: any) => acc + Number(d.value ?? 0),
    0,
  );

  return (
    <Card>
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <Target className="h-4 w-4 text-primary" /> Oportunidades
            {rows.length > 0 && (
              <Badge variant="secondary" className="ml-1">
                {rows.length}
              </Badge>
            )}
          </CardTitle>
          {rows.length > 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              <span className="tabular-nums font-medium text-foreground">
                {formatBRL(totalOpen)}
              </span>{" "}
              em aberto · {formatBRL(totalWon)} ganho
            </p>
          )}
        </div>
        <Link href="/crm/deals">
          <Button size="sm" variant="outline">
            Abrir CRM
          </Button>
        </Link>
      </CardHeader>
      <CardContent className="space-y-2">
        {dealsQuery.isLoading && (
          <div className="space-y-2">
            <div className="h-16 rounded-lg bg-muted animate-pulse" />
            <div className="h-16 rounded-lg bg-muted animate-pulse" />
          </div>
        )}
        {!dealsQuery.isLoading && rows.length === 0 && (
          <div className="text-center py-8 border-2 border-dashed rounded-lg">
            <Target className="h-8 w-8 mx-auto mb-2 text-muted-foreground opacity-40" />
            <p className="text-sm text-muted-foreground mb-3">
              Este contato ainda não tem oportunidades no funil.
            </p>
            <Link href="/crm/deals">
              <Button size="sm" variant="outline">
                <Briefcase className="h-3.5 w-3.5 mr-1.5" />
                Criar oportunidade
              </Button>
            </Link>
          </div>
        )}
        {rows.map((d: any) => {
          const pipeline = pipelines.find((p: any) => p.id === d.pipelineId);
          const isWon = d.status === "won";
          const isLost = d.status === "lost";
          return (
            <Link
              key={d.id}
              href={`/crm/deals/${d.id}`}
              className={`block rounded-lg border p-3 hover:bg-muted/40 transition relative overflow-hidden ${
                isWon
                  ? "border-emerald-200 dark:border-emerald-900"
                  : isLost
                  ? "border-red-200 dark:border-red-900 opacity-70"
                  : ""
              }`}
            >
              {isWon && (
                <div className="absolute top-0 left-0 w-1 h-full bg-emerald-500" />
              )}
              {isLost && <div className="absolute top-0 left-0 w-1 h-full bg-red-500" />}
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{d.title}</div>
                  <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-muted-foreground">
                    {isWon && (
                      <Badge className="bg-emerald-600 h-5 text-[10px]">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Fechada
                      </Badge>
                    )}
                    {isLost && (
                      <Badge className="bg-red-600 h-5 text-[10px]">
                        <XCircle className="h-3 w-3 mr-1" />
                        Perdida
                      </Badge>
                    )}
                    {!isWon && !isLost && (
                      <Badge variant="outline" className="h-5 text-[10px]">
                        Em aberto
                      </Badge>
                    )}
                    {pipeline?.name && <span>{pipeline.name}</span>}
                    {d.expectedCloseDate && !isWon && !isLost && (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(d.expectedCloseDate).toLocaleDateString("pt-BR")}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-base font-bold tabular-nums">
                    {formatBRL(d.value)}
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </CardContent>
    </Card>
  );
}
